<script>
import simplebar from "simplebar-vue";
import SideNav from "./side-nav";
import { layoutComputed } from "@/state/helpers";
import { mapState } from "vuex";

/**
 * Sidebar component - Thiết kế mới hiện đại
 * Giữ nguyên toàn bộ logic: MetisMenu, type/width watcher, onRoutechange
 */
export default {
  components: { simplebar, SideNav },
  props: {
    isCondensed: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    width: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...layoutComputed,
    ...mapState("authStore", ["currentUserAuth"]),
  },
  data() {
    return {
      settings: {
        minScrollbarLength: 60,
      },
    };
  },
  methods: {
    onRoutechange() {
      setTimeout(() => {
        if (document.getElementsByClassName("mm-active").length > 0) {
          const currentPosition =
            document.getElementsByClassName("mm-active")[0].offsetTop;
          if (currentPosition > 500)
            this.$refs.currentMenu.SimpleBar.getScrollElement().scrollTop =
              currentPosition + 300;
        }
      }, 300);
    },
    handleLogout() {
      this.$store.dispatch("authStore/logout");
    },
  },
  watch: {
    $route: {
      handler: "onRoutechange",
      immediate: true,
      deep: true,
    },
    type: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          switch (newVal) {
            case "dark":
              document.body.setAttribute("data-sidebar", "dark");
              document.body.removeAttribute("data-topbar");
              document.body.removeAttribute("data-sidebar-size");
              document.body.removeAttribute("data-keep-enlarged");
              document.body.classList.remove("vertical-collpsed");
              break;
            case "light":
              document.body.setAttribute("data-topbar", "dark");
              document.body.removeAttribute("data-sidebar");
              document.body.removeAttribute("data-sidebar-size");
              document.body.classList.remove("vertical-collpsed");
              break;
            case "compact":
              document.body.setAttribute("data-sidebar-size", "small");
              document.body.setAttribute("data-sidebar", "dark");
              document.body.classList.remove("vertical-collpsed");
              document.body.removeAttribute("data-topbar", "dark");
              break;
            case "icon":
              document.body.setAttribute("data-keep-enlarged", "true");
              document.body.classList.add("vertical-collpsed");
              document.body.setAttribute("data-sidebar", "dark");
              document.body.removeAttribute("data-topbar", "dark");
              break;
            case "colored":
              document.body.setAttribute("data-sidebar", "colored");
              document.body.removeAttribute("data-keep-enlarged");
              document.body.classList.remove("vertical-collpsed");
              document.body.removeAttribute("data-sidebar-size");
              break;
            default:
              document.body.setAttribute("data-sidebar", "dark");
              break;
          }
        }
      },
    },
    width: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          switch (newVal) {
            case "boxed":
              document.body.setAttribute("data-layout-size", "boxed");
              document.body.removeAttribute("data-layout-scrollable");
              break;
            case "fluid":
              document.body.setAttribute("data-layout-mode", "fluid");
              document.body.removeAttribute("data-layout-size");
              document.body.removeAttribute("data-layout-scrollable");
              break;
            case "scrollable":
              document.body.setAttribute("data-layout-scrollable", "true");
              document.body.removeAttribute("data-layout-mode");
              document.body.removeAttribute("data-layout-size");
              break;
            default:
              document.body.setAttribute("data-layout-mode", "fluid");
              break;
          }
        }
      },
    },
  },
};
</script>

<template>
  <div class="vertical-menu admin-sidebar-new">

    <!-- Logo Brand Header ẩn vì NavBar đã chứa logo -->
    <!-- <div class="sidebar-brand-header">...</div> -->

    <!-- ===== MENU LIST (Giữ nguyên logic SideNav + MetisMenu) ===== -->
    <simplebar
      v-if="!isCondensed"
      :settings="settings"
      class="sidebar-scroll-area"
      ref="currentMenu"
      id="my-element"
    >
      <SideNav />
    </simplebar>

    <div v-else class="condensed-menu-container">
      <SideNav />
    </div>

    <!-- ===== FOOTER USER PROFILE ===== -->
    <div class="sidebar-footer" v-if="currentUserAuth">
      <div class="user-profile-row" :class="{ 'justify-center': isCondensed }">
        <div class="user-avatar-box">
          <span class="isax isax-profile-circle" style="font-size: 28px;"></span>
        </div>
        <div v-if="!isCondensed" class="user-info">
          <p class="user-name">{{ currentUserAuth.fullName || currentUserAuth.userName }}</p>
          <p class="user-role">@{{ currentUserAuth.userName }}</p>
        </div>
        <button
          v-if="!isCondensed"
          @click="handleLogout"
          class="btn-logout"
          title="Đăng xuất"
        >
          <span class="isax isax-logout-1"></span>
        </button>
      </div>
    </div>

  </div>
</template>

<style scoped>
/* =========================================
   ADMIN SIDEBAR
   Đồng bộ palette với HeaderNavBar (public)
   Sử dụng biến CSS chuẩn từ variables.css:
   --primary, --surface, --on-surface, --on-surface-variant
   ========================================= */

.admin-sidebar-new {
  display: flex;
  flex-direction: column;
  background-color: var(--surface-container-lowest, #ffffff);
  overflow: hidden;
}

/* ===== SCROLL AREA ===== */
.sidebar-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Scrollbar tùy chỉnh mảnh nhẹ */
.sidebar-scroll-area::-webkit-scrollbar {
  width: 3px;
}
.sidebar-scroll-area::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-scroll-area::-webkit-scrollbar-thumb {
  background: var(--surface-dim, rgba(112, 122, 108, 0.15));
  border-radius: 4px;
}
.sidebar-scroll-area::-webkit-scrollbar-thumb:hover {
  background: var(--on-surface-variant, rgba(112, 122, 108, 0.3));
}

/* ===== CONDENSED STATE ===== */
.condensed-menu-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ===== FOOTER USER PROFILE ===== */
.sidebar-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--surface-dim, #d7dbd2);
  background-color: var(--surface-container-low, #f1f5eb);
  flex-shrink: 0;
}

.user-profile-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-profile-row.justify-center {
  justify-content: center;
}

.user-avatar-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(13, 99, 27, 0.08);
  color: var(--primary, #0d631b);
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  overflow: hidden;
}

.user-name {
  font-family: var(--font-body, "Inter");
  font-size: 12px;
  font-weight: 700;
  color: var(--on-surface, #181d17);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-family: var(--font-body, "Inter");
  font-size: 10px;
  color: var(--primary, #0d631b);
  font-weight: 500;
  margin: 2px 0 0 0;
}

.btn-logout {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--rounded-default, 8px);
  border: none;
  background: transparent;
  color: var(--error, #ba1a1a);
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;
  font-size: 18px;
}

.btn-logout:hover {
  background-color: rgba(186, 26, 26, 0.08);
}
</style>